// Post-submission customer feedback / Google-review prompt.
// Rendered on Success.jsx (home-delivery) and PickupSuccess.jsx.
// Design brief: 5 smiley-face rating buttons, thank-you screen for 1-3,
// Google-review invitation with the exact client-supplied URL for 4-5.
// All server-side — no client-only rating record.
//
// Feb-2026 — surgical additive component. Does not alter any existing
// invoice / payment / admin flow.

import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star } from "lucide-react";
import api, { formatError } from "@/lib/api";
import ReviewAssistant from "./ReviewAssistant";
const POSITIVE_THRESHOLD = 4;

// Emotion metadata for the five smiley buttons. Values 1-5 map to the
// backend rating field. Each SVG is drawn inline so the artwork stays
// crisp at any DPR and matches the ivory/maroon Tanish palette without
// external assets.
const SMILEYS = [
  { value: 1, label: "Very unhappy", face: "very-sad",   ring: "from-[#F5D3D3]" },
  { value: 2, label: "Unhappy",      face: "sad",        ring: "from-[#F1DFC4]" },
  { value: 3, label: "Neutral",      face: "neutral",    ring: "from-[#F0E9DA]" },
  { value: 4, label: "Happy",        face: "happy",      ring: "from-[#E4EFDC]" },
  { value: 5, label: "Very happy",   face: "very-happy", ring: "from-[#D6E9D5]" },
];

function SmileyFace({ face, className = "w-8 h-8 sm:w-11 sm:h-11" }) {
  // Simple, hand-drawn feeling face. Same stroke width for consistency;
  // the mouth path is what differentiates each emotion. Size is driven
  // by Tailwind classes on the SVG element so the caller can scale
  // responsively (mobile 32px → desktop 44px) without prop drilling.
  const stroke = "#8A2A2A";
  const fill = "#FFF7EF";
  let mouth;
  let eyes = (
    <>
      <circle cx="35" cy="42" r="3.2" fill={stroke} />
      <circle cx="65" cy="42" r="3.2" fill={stroke} />
    </>
  );
  switch (face) {
    case "very-sad":
      mouth = <path d="M 32 72 Q 50 55 68 72" stroke={stroke} strokeWidth="3.4" fill="none" strokeLinecap="round" />;
      eyes = (
        <>
          <path d="M 30 40 L 40 46 M 40 40 L 30 46" stroke={stroke} strokeWidth="2.6" strokeLinecap="round" />
          <path d="M 60 40 L 70 46 M 70 40 L 60 46" stroke={stroke} strokeWidth="2.6" strokeLinecap="round" />
        </>
      );
      break;
    case "sad":
      mouth = <path d="M 34 70 Q 50 60 66 70" stroke={stroke} strokeWidth="3.4" fill="none" strokeLinecap="round" />;
      break;
    case "neutral":
      mouth = <line x1="34" y1="68" x2="66" y2="68" stroke={stroke} strokeWidth="3.4" strokeLinecap="round" />;
      break;
    case "happy":
      mouth = <path d="M 34 62 Q 50 76 66 62" stroke={stroke} strokeWidth="3.4" fill="none" strokeLinecap="round" />;
      break;
    case "very-happy":
    default:
      mouth = <path d="M 30 58 Q 50 82 70 58 Q 50 68 30 58 Z" fill={stroke} />;
      eyes = (
        <>
          <path d="M 30 40 Q 35 32 40 40" stroke={stroke} strokeWidth="3" fill="none" strokeLinecap="round" />
          <path d="M 60 40 Q 65 32 70 40" stroke={stroke} strokeWidth="3" fill="none" strokeLinecap="round" />
        </>
      );
  }
  return (
    <svg viewBox="0 0 100 100" className={className} aria-hidden="true">
      <circle cx="50" cy="50" r="46" fill={fill} stroke={stroke} strokeWidth="2.4" />
      {eyes}
      {mouth}
    </svg>
  );
}

export default function FeedbackPrompt({ submissionId, group }) {
  // Feb-2026 · eligibility gate: only Group B (BRIDAL) customers see
  // the post-submission feedback + Google-Review flow. Group A (SIDER)
  // and any row missing an authoritative group classification fall
  // through to render nothing so the existing success screen is
  // unaffected. Group comes from the server-authoritative
  // POST /submissions response (never inferred client-side from the
  // invoice number) and is threaded through the navigate() state.
  const eligible = group === "B";
  const [rating, setRating] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [hovered, setHovered] = useState(null);
  // Optional low-score note (ratings 1-3 only). Two-state so we can
  // switch to a "thanks, noted" acknowledgement after submit.
  const [note, setNote] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false);

  // Detect an existing rating on mount so a page refresh does NOT invite
  // the customer to rate a second time (idempotency belt-and-braces on
  // top of the server-side upsert).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!submissionId) return;
      try {
        const r = await api.get(`/submissions/${submissionId}/feedback`);
        if (!cancelled && r && r.status === 200 && r.data?.rating) {
          setRating(r.data.rating);
          setSaved(true);
          if (r.data.note) {
            setNote(r.data.note);
            setNoteSaved(true);
          }
        }
      } catch { /* 204 or transient — safe to ignore */ }
    })();
    return () => { cancelled = true; };
  }, [submissionId]);

  async function pick(v) {
    if (!submissionId || saving) return;
    setError("");
    setRating(v);
    setSaving(true);
    try {
      await api.post(`/submissions/${submissionId}/feedback`, { rating: v });
      setSaved(true);
    } catch (e) {
      setError(formatError(e) || "Couldn't save your rating. Please try again.");
      setRating(null);
    } finally {
      setSaving(false);
    }
  }

  async function submitNote() {
    const trimmed = note.trim();
    if (!trimmed || !rating || noteSaving) return;
    setError("");
    setNoteSaving(true);
    try {
      await api.post(`/submissions/${submissionId}/feedback`, {
        rating,
        note: trimmed,
      });
      setNoteSaved(true);
    } catch (e) {
      setError(formatError(e) || "Couldn't send your note. Please try again.");
    } finally {
      setNoteSaving(false);
    }
  }

  if (!submissionId || !eligible) return null;

  const isPositive = rating != null && rating >= POSITIVE_THRESHOLD;

  return (
    <div
      data-testid="feedback-prompt"
      className="mt-10 bg-white border border-line rounded-3xl px-5 sm:px-8 py-8 sm:py-10"
    >
      <AnimatePresence mode="wait">
        {!saved && (
          <motion.div
            key="pick"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="text-center"
          >
            <p className="overline text-[10px]">Feedback</p>
            <h3 className="font-serif text-2xl sm:text-3xl text-ink mt-2 leading-tight">
              How much would you like to rate us?
            </h3>
            <p className="text-sm text-muted2 mt-2 max-w-md mx-auto leading-relaxed">
              We&rsquo;d love to know how your experience was.
            </p>

            <div
              className="mt-7 flex flex-nowrap items-center justify-center gap-1.5 sm:gap-4"
              role="radiogroup"
              aria-label="Rate your experience from 1 to 5"
            >
              {SMILEYS.map((s) => {
                const isActive = rating === s.value;
                const isHover = hovered === s.value;
                return (
                  <button
                    key={s.value}
                    type="button"
                    role="radio"
                    aria-checked={isActive}
                    aria-label={s.label}
                    disabled={saving}
                    data-testid={`feedback-smiley-${s.value}`}
                    onClick={() => pick(s.value)}
                    onMouseEnter={() => setHovered(s.value)}
                    onMouseLeave={() => setHovered(null)}
                    className={[
                      "relative rounded-full p-0.5 shrink-0 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-maroon/40",
                      isActive || isHover ? "scale-110" : "scale-100",
                      saving && !isActive ? "opacity-40" : "opacity-100",
                    ].join(" ")}
                  >
                    <span
                      className={`block rounded-full bg-gradient-to-b ${s.ring} to-white p-1 sm:p-2 border border-line`}
                    >
                      <SmileyFace face={s.face} />
                    </span>
                  </button>
                );
              })}
            </div>
            {error && (
              <p data-testid="feedback-error" className="mt-4 text-xs text-[#8A2A2A]">
                {error}
              </p>
            )}
          </motion.div>
        )}

        {saved && !isPositive && (
          <motion.div
            key="thanks"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="text-center"
            data-testid="feedback-thanks-lowscore"
          >
            <h3 className="font-serif text-2xl sm:text-3xl text-ink leading-tight">
              Thank you for your feedback! <span aria-hidden="true">❤️</span>
            </h3>
            <p className="text-sm text-muted2 mt-3 max-w-md mx-auto leading-relaxed">
              Your feedback is valuable to us.
            </p>

            {/* Optional one-line note. Only shown for low scores so we
                collect actionable context without pestering happy
                customers. Sent via a second POST that carries the
                already-saved rating + the note; the backend upsert
                keeps everything on the single feedback row. */}
            {!noteSaved && (
              <div className="mt-6 text-left max-w-md mx-auto">
                <label
                  htmlFor="feedback-note-input"
                  className="overline text-[10px] block mb-2 text-center"
                >
                  What could we do better? <span className="text-muted2 lowercase tracking-normal">(optional)</span>
                </label>
                <textarea
                  id="feedback-note-input"
                  data-testid="feedback-note-input"
                  value={note}
                  onChange={(e) => setNote(e.target.value.slice(0, 500))}
                  disabled={noteSaving}
                  rows={3}
                  maxLength={500}
                  placeholder="One line is enough — we read every message."
                  className="w-full text-sm text-ink placeholder:text-muted2/70 bg-ivory/60 border border-line rounded-2xl px-4 py-3 leading-relaxed focus:outline-none focus:ring-2 focus:ring-maroon/25 focus:border-maroon/40 resize-none"
                />
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-[10px] text-muted2 tabular-nums">
                    {note.trim().length}/500
                  </span>
                  <button
                    type="button"
                    data-testid="feedback-note-submit"
                    onClick={submitNote}
                    disabled={!note.trim() || noteSaving}
                    className="btn-outline-maroon !text-[10px] tracking-[0.24em] uppercase !px-4 !py-2 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {noteSaving ? "Sending…" : "Send"}
                  </button>
                </div>
                {error && (
                  <p data-testid="feedback-note-error" className="mt-2 text-xs text-[#8A2A2A] text-center">
                    {error}
                  </p>
                )}
              </div>
            )}
            {noteSaved && (
              <p
                data-testid="feedback-note-thanks"
                className="mt-6 text-sm text-muted2 max-w-md mx-auto italic"
              >
                Thanks — we&rsquo;ve noted your comment and will look into it.
              </p>
            )}
          </motion.div>
        )}

        {saved && isPositive && (
          <motion.div
            key="google"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="text-center"
            data-testid="feedback-thanks-google"
          >
            <h3 className="font-serif text-2xl sm:text-3xl text-ink leading-tight">
              Thank you for your feedback! <span aria-hidden="true">❤️</span>
            </h3>
            <p className="text-sm sm:text-base text-muted2 mt-3 max-w-md mx-auto leading-relaxed">
              Would you like to rate us on Google?
            </p>
            <div className="mt-4 flex items-center justify-center gap-1 text-[#D4A017]">
              {[1,2,3,4,5].map((i) => (
                <Star key={i} size={22} strokeWidth={1.4} fill="#D4A017" />
              ))}
            </div>
            {/* BRIDAL (Group B) only: the AI-assisted review helper owns the
                single shared copy-then-Google CTA. SIDER never reaches this
                branch because the eligibility gate returns null for Group A. */}
            <ReviewAssistant submissionId={submissionId} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
